"use client";

import Link from "next/link";
import type { Route } from "next";
import { motion } from "framer-motion";
import { ArrowRight, Sparkles } from "lucide-react";
import type { MouseEvent } from "react";

import type { ClarificationChip } from "@/lib/api/clarify";
import { MOTION, STAGGER, ms } from "@/lib/design-tokens";
import { prettifyRetailerSlug } from "@/lib/retailers/prettify";
import { cn } from "@/lib/utils";

interface Props {
  chip: ClarificationChip;
  index: number;
  /** When true, renders the chip in the bento grid's full-width "hero"
   * slot - bigger type, amber-tinted glass + glow, a solid Compare button.
   * Used for the agent's recommended pick. */
  isHero?: boolean;
  postcode: string;
  /** Optional click interceptor. When provided, the parent handles the
   * navigation (so it can immediately swap the grid for the searching
   * skeleton before Next.js navigates). The Link href stays set so
   * middle-click / cmd-click still open in a new tab. */
  onPick?: (chip: ClarificationChip) => void;
}

/**
 * One chip card in the clarification bento grid (2026-06-28 redesign).
 *
 * Click navigates to /compare with the chip's specific product_label as
 * the new query (`from_chip=1` so clarify is skipped and the paid search
 * runs). Visual: glass card, no icon - typography + the price/CTA carry it.
 * The dead "—" placeholder is gone: a card shows either a real
 * "from $X at <Retailer>" (when a preview price exists) or a clean
 * "Compare prices" call to action.
 */

/**
 * The backend already sends the preview price pre-formatted as the STRING
 * "from $1799" (clarification._lookup_preview_price). Pull the number out and
 * reformat with thousands separators -> "from $1,799".
 *
 * Do NOT run this through formatAUD(): that expects a bare number, so
 * parseFloat("from $1799") is NaN and it prints "—". That bug is the reason
 * the price never rendered on the old card.
 */
function formatPreviewPrice(raw: string): string {
  const m = raw.match(/(\d[\d,]*)/);
  if (!m) return raw;
  const n = Number(m[1].replace(/,/g, ""));
  if (!Number.isFinite(n)) return raw;
  return `from ${n.toLocaleString("en-AU", {
    style: "currency",
    currency: "AUD",
    maximumFractionDigits: 0,
  })}`;
}

function PriceOrCta({
  chip,
  isHero,
}: {
  chip: ClarificationChip;
  isHero?: boolean;
}) {
  if (chip.preview_price_aud) {
    const retailer = chip.preview_retailer
      ? prettifyRetailerSlug(chip.preview_retailer)
      : null;
    return (
      <span className="flex min-w-0 items-baseline gap-2">
        <span
          className={cn(
            "font-semibold text-accent",
            isHero ? "text-lg" : "text-sm",
          )}
        >
          {formatPreviewPrice(chip.preview_price_aud)}
        </span>
        {retailer ? (
          <span className="truncate text-xs text-muted-foreground">
            at {retailer}
          </span>
        ) : null}
      </span>
    );
  }
  return (
    <span
      className={cn(
        "font-medium text-foreground/80",
        isHero ? "text-base" : "text-sm",
      )}
    >
      {isHero ? "See prices across AU retailers" : "Compare prices"}
    </span>
  );
}

export function ChipCard({
  chip,
  index,
  isHero = false,
  postcode,
  onPick,
}: Props) {
  const href =
    `/compare?q=${encodeURIComponent(chip.product_label)}&postcode=${encodeURIComponent(postcode)}&from_chip=1` as Route;

  const handleClick = (e: MouseEvent<HTMLAnchorElement>) => {
    if (!onPick) return;
    if (
      e.metaKey ||
      e.ctrlKey ||
      e.shiftKey ||
      e.altKey ||
      e.button !== 0
    ) {
      return;
    }
    e.preventDefault();
    onPick(chip);
  };

  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.96, y: 6 }}
      animate={{ opacity: 1, scale: 1, y: 0 }}
      transition={{
        duration: ms(MOTION.smooth),
        ease: [0.16, 1, 0.3, 1],
        delay: 0.1 + (index * STAGGER.chips) / 1000,
      }}
      className={cn("h-full", isHero && "sm:col-span-2")}
    >
      <Link
        href={href}
        onClick={handleClick}
        className={cn(
          "group relative flex h-full flex-col overflow-hidden text-left",
          "transition-all duration-200 active:scale-[0.985]",
          isHero
            ? "rounded-[22px] border border-accent/25 bg-accent/[0.06] p-6 backdrop-blur-xl hover:-translate-y-0.5 hover:border-accent/50 sm:p-7"
            : "min-h-[158px] rounded-[18px] border border-border bg-secondary p-5 backdrop-blur-md hover:-translate-y-0.5 hover:border-accent/40 hover:bg-secondary",
        )}
      >
        {isHero ? (
          <div className="pointer-events-none absolute -right-16 -top-16 h-44 w-44 rounded-full bg-accent/20 blur-3xl" />
        ) : null}

        <div className="relative flex h-full flex-col">
          {chip.is_recommended || chip.tag ? (
            <div className="mb-3.5 flex flex-wrap items-center gap-2">
              {chip.is_recommended ? (
                <span className="inline-flex items-center gap-1 rounded-full border border-accent/30 bg-accent/15 px-2.5 py-1 text-[12px] font-semibold uppercase tracking-wider text-accent">
                  <Sparkles className="h-2.5 w-2.5" aria-hidden="true" />
                  Recommended
                </span>
              ) : null}
              {chip.tag ? (
                <span className="inline-flex items-center rounded-full border border-border bg-secondary px-2.5 py-1 text-[12px] font-semibold uppercase tracking-wider text-foreground/75">
                  {chip.tag}
                </span>
              ) : null}
            </div>
          ) : null}

          <h3
            className={cn(
              "font-semibold tracking-tight text-foreground",
              isHero ? "text-2xl sm:text-3xl" : "text-lg",
            )}
          >
            {chip.product_label}
          </h3>
          <p className="mt-1.5 text-sm leading-snug text-muted-foreground first-letter:uppercase">
            {chip.one_line}
          </p>

          <div className="min-h-[14px] flex-1" />

          <div className="mt-4 border-t border-border" />
          <div className="mt-3.5 flex items-center justify-between gap-3">
            <PriceOrCta chip={chip} isHero={isHero} />
            {isHero ? (
              <span className="inline-flex shrink-0 items-center gap-1.5 rounded-full bg-accent px-4 py-2 text-sm font-semibold text-[#1a1306] transition-transform duration-150 group-hover:translate-x-0.5">
                Compare
                <ArrowRight className="h-4 w-4" aria-hidden="true" />
              </span>
            ) : (
              <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full border border-border text-foreground/50 transition-all duration-150 group-hover:border-accent group-hover:bg-accent group-hover:text-[#1a1306]">
                <ArrowRight className="h-3.5 w-3.5" aria-hidden="true" />
              </span>
            )}
          </div>
        </div>
      </Link>
    </motion.div>
  );
}
